// Phase 85.A.72 — Non-dismissable modal forcing athlete to either upload
// a face-detect-passed avatar OR defer 24h.
//
// User choices:
//   Q5 = c — 2 nút "Đồng ý cập nhật ngay" + "Để sau 24h" (defer).
//   Q6 = a — Khi click "Đồng ý", buộc upload ảnh mới TRONG chính modal;
//            modal sẽ tự đóng khi BE confirm pass face-detect & mark
//            proposal `completed`.
//   Q7 = a — Avatar upload mới VẪN phải pass face-detect Gemini.
//   Q1 = d — Banner đỏ inline khi face-detect reject + carousel guide
//            sau 2 fail liên tiếp.
//
// API: GET /api/athlete/avatar-proposals (list active)
//      PATCH /api/athlete/avatar-proposals/{id} {decision: accepted|deferred}
//      POST /auth/me/avatar (upload) — share flow với UpdateProfileModal.
import { useEffect, useRef, useState } from "react";
import { m as motion, AnimatePresence } from "framer-motion";
import {
  Warning, CameraPlus, Spinner, ShieldCheck, ArrowRight, UserCircle, ImageSquare,
  Phone,
} from "@phosphor-icons/react";

// v16.17 Phase C.14.b — Zalo Wikipedia icon URL (same as ZaloFloatButton).
const ZALO_ICON_URL = "https://upload.wikimedia.org/wikipedia/commons/9/91/Icon_of_Zalo.svg";
import { toast } from "sonner";

import { api } from "../../lib/api";
import { parseZaloContact } from "../../lib/zaloHelpers";
import { cropAndResizeImage } from "../../lib/imageProcessing";
import AvatarFailureGuideModal from "./AvatarFailureGuideModal";
import UploadConsentWizard from "../shared/UploadConsentWizard";
import AIConsentModal from "../moderation/AIConsentModal";
import AvatarAIResultModal from "./profile/AvatarAIResultModal";

const CHIP_LABEL = {
  blurry: "Ảnh mờ/không rõ",
  not_you: "Không phải bạn",
  logo: "Logo/biểu tượng",
  group: "Ảnh nhóm",
  other: "Lý do khác",
};

const FAIL_KEY = "pickleague:avatar_proposal_fail_count";

function readFailCount() {
  try {
    return Number(sessionStorage.getItem(FAIL_KEY) || 0);
  } catch {
    return 0;
  }
}

function bumpFailCount() {
  try {
    const next = readFailCount() + 1;
    sessionStorage.setItem(FAIL_KEY, String(next));
    return next;
  } catch {
    return 0;
  }
}

function resetFailCount() {
  try {
    sessionStorage.removeItem(FAIL_KEY);
  } catch {
    /* ignore */
  }
}

export default function AvatarProposalRequiredModal({
  proposal,        // { id, reason, reason_chips, sample_image_url, proposed_by_name }
  onResolved,      // () => void  — call sau khi accept/defer thành công
}) {
  const [phase, setPhase] = useState("intro"); // intro | upload
  const [busy, setBusy] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [rejectInfo, setRejectInfo] = useState(null); // {reason, confidence}
  const [showGuide, setShowGuide] = useState(false);
  // Phase 85.A.87 — Gating consent: wizard auto-hiện khi vào phase upload,
  // user kéo slider qua threshold → consentPassed=true → wizard tắt → user
  // tự click button "Chọn ảnh chân dung mới" (trực tiếp gọi fileRef.click)
  // → iOS Safari accept user-gesture chain.
  const [consentPassed, setConsentPassed] = useState(false);
  // v16.60.16 — Apple 5.1.1(ii): gate AI consent trước consent wizard.
  const [aiConsentPending, setAiConsentPending] = useState(false);
  // v16.61.2 — "granted" | "declined". Declined → bỏ wizard, upload thường.
  const [aiConsent, setAiConsent] = useState("granted");
  // Phase 85.A.89 — Progress tracking.
  const [uploadProgress, setUploadProgress] = useState({ stage: "idle", pct: 0, label: "" });
  // v16.61.3 — Popup kết quả AI xét duyệt (PASS/FAIL/SKIPPED).
  const [aiResult, setAiResult] = useState(null);
  const fileRef = useRef(null);

  // v16 — Fetch global Zalo BTC contacts để VĐV liên hệ xác minh điểm trình.
  // v16.5 — Ưu tiên `proposal.verify_zalos` snapshot; chỉ fallback global
  // list khi admin không đính kèm gì (giữ tương thích ngược).
  const [zaloBtcContacts, setZaloBtcContacts] = useState([]);
  const snapshotZalos = Array.isArray(proposal?.verify_zalos) ? proposal.verify_zalos : [];
  useEffect(() => {
    if (snapshotZalos.length > 0) {
      setZaloBtcContacts([]);
      return () => {};
    }
    let cancelled = false;
    api.get("/athlete/global-zalo-contacts")
      .then((r) => {
        if (cancelled) return;
        const items = (r?.data?.items || [])
          .map((m) => ({ ...m, zalo: parseZaloContact(m.contact) }))
          .filter((m) => !!m.zalo);
        setZaloBtcContacts(items);
      })
      .catch(() => { /* silent */ });
    return () => { cancelled = true; };
  }, [snapshotZalos.length]);

  useEffect(() => {
    if (!proposal) {
      setPhase("intro");
      setRejectInfo(null);
      setConsentPassed(false);
      setAiConsentPending(false);
      setAiConsent("granted");
    } else if (proposal.status === "accepted") {
      // Phase 85.A.235 — Proposal đã "accepted" nhưng chưa upload (resurfaced
      // sau F5/mở lại) → mở thẳng phase upload, không bắt bấm "Đồng ý" lại.
      setPhase("upload");
      // v16.61.1 — LUÔN hỏi AI consent (không cache).
      setAiConsentPending(true);
    } else {
      setPhase("intro");
    }
  }, [proposal?.id]);

  const chips = (proposal?.reason_chips || []).map((c) => CHIP_LABEL[c] || c);

  const handleAccept = async () => {
    if (!proposal || busy) return;
    setBusy(true);
    try {
      await api.patch(`/athlete/avatar-proposals/${proposal.id}`, { decision: "accepted" });
      setPhase("upload");
      // v16.61.1 — LUÔN hỏi AI consent (không cache).
      setAiConsentPending(true);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Xác nhận thất bại");
    } finally {
      setBusy(false);
    }
  };

  // Phase 85.A.87 — Click trực tiếp file picker SAU khi consent passed.
  // KHÔNG đặt qua setTimeout — phải sync trong cùng user-gesture tap để
  // iOS Safari chấp nhận.
  const onPickFile = () => {
    if (!consentPassed) {
      // Defensive recovery: nếu state gating bị kẹt (vd AI consent modal
      // từng bị che bởi z-index thấp hơn), tap nút sẽ mở lại AI consent
      // → wizard → pick file. KHÔNG return im lặng làm user tưởng nút hỏng.
      setAiConsentPending(true);
      return;
    }
    fileRef.current?.click();
  };

  const onFileChange = async (e) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (!file) return;
    if (file.size > 18 * 1024 * 1024) {
      toast.error("Ảnh quá lớn (>18 MiB)");
      return;
    }
    setUploading(true);
    setRejectInfo(null);
    setUploadProgress({ stage: "compressing", pct: 5, label: "Đang chuẩn bị ảnh…" });
    let analyzeTimer = null;
    try {
      // Phase 85.A.89 — Progress: compress.
      setUploadProgress({ stage: "compressing", pct: 8, label: "Đang crop & resize ảnh…" });
      const processed = await cropAndResizeImage(file, 1440);
      const fd = new FormData();
      fd.append("file", processed, "avatar.jpg");
      fd.append("ai_consent", aiConsent);
      setUploadProgress({ stage: "uploading", pct: 12, label: "Đang tải ảnh lên (0%)…" });
      const r = await api.post("/auth/me/avatar", fd, {
        headers: { "Content-Type": "multipart/form-data" },
        onUploadProgress: (p) => {
          const total = p.total || processed.size || 1;
          const ratio = Math.min(1, (p.loaded || 0) / total);
          const pct = Math.round(12 + ratio * 68);
          setUploadProgress({
            stage: "uploading",
            pct,
            label: `Đang tải ảnh lên (${Math.round(ratio * 100)}%)…`,
          });
          if (ratio >= 1 && !analyzeTimer) {
            setUploadProgress({
              stage: "analyzing",
              pct: 82,
              label: aiConsent === "declined" ? "Đang lưu ảnh…" : "Đang phân tích khuôn mặt bằng AI…",
            });
            const startedAt = Date.now();
            analyzeTimer = setInterval(() => {
              const elapsed = (Date.now() - startedAt) / 1000;
              const targetPct = Math.min(95, 82 + elapsed * 1.1);
              const label = aiConsent === "declined"
                ? "Đang lưu ảnh…"
                : elapsed < 3
                ? "Đang phân tích khuôn mặt bằng AI…"
                : elapsed < 7
                ? "Đang đối chiếu chất lượng ảnh…"
                : "Sắp xong, đang lưu vào hồ sơ…";
              setUploadProgress({ stage: "analyzing", pct: targetPct, label });
            }, 500);
          }
        },
      });
      if (analyzeTimer) clearInterval(analyzeTimer);
      setUploadProgress({ stage: "done", pct: 100, label: "Hoàn tất!" });
      resetFailCount();
      // v16.61.3 — Mở popup kết quả AI (thay cho toast). Sau khi user
      // đóng popup, mới gọi onResolved để đóng proposal modal.
      const air = r?.data?.ai_result;
      setAiResult({
        verdict: air?.verdict || (aiConsent === "declined" ? "skipped" : "pass"),
        reason: air?.reason || null,
        confidence: air?.confidence ?? null,
        _resolvedPayload: r?.data,
      });
    } catch (err) {
      if (analyzeTimer) clearInterval(analyzeTimer);
      setUploadProgress({ stage: "error", pct: 0, label: "" });
      const detail = err?.response?.data?.detail;
      const isFaceReject =
        err?.response?.status === 422
        && typeof detail === "object"
        && detail?.code === "FACE_REJECTED";
      if (isFaceReject) {
        const reason = detail?.reason || "Ảnh không phát hiện được khuôn mặt người.";
        setRejectInfo({ reason, confidence: detail?.confidence });
        bumpFailCount();
        // v16.61.3 — Mở popup kết quả AI (fail). BỎ auto-open
        // AvatarFailureGuideModal sau 2 fail (làm user tưởng lỗi).
        // Muốn thử lại → user tự bấm nút "Chọn ảnh chân dung mới" lần
        // nữa → wizard hướng dẫn hiện lại như thường.
        setAiResult({
          verdict: "fail",
          reason,
          confidence: detail?.confidence ?? null,
        });
      } else {
        toast.error(
          typeof detail === "string"
            ? detail
            : detail?.message || err.message || "Upload thất bại"
        );
      }
    } finally {
      setUploading(false);
    }
  };

  if (!proposal) return null;

  return (
    <>
      <AnimatePresence>
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          className="pl-fixed-fullscreen z-[350] flex items-end sm:items-center justify-center bg-black/85 backdrop-blur-md px-3 pb-3 sm:pb-0"
          data-testid="avatar-proposal-required-modal"
        >
          <motion.div
            initial={{ y: 40, scale: 0.96, opacity: 0 }}
            animate={{ y: 0, scale: 1, opacity: 1 }}
            exit={{ y: 40, scale: 0.96, opacity: 0 }}
            transition={{ duration: 0.25 }}
            className="w-full max-w-md rounded-3xl border border-amber-400/40 bg-gradient-to-br from-[#1A1208] via-[#0F1623] to-[#0A0F1C] shadow-[0_24px_80px_rgba(255,138,0,0.4)] overflow-hidden max-h-[92dvh] flex flex-col"
          >
            <div className="px-5 py-4 flex items-start gap-3 border-b border-white/8 bg-gradient-to-r from-amber-500/12 to-transparent">
              <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-500/40 to-orange-500/20 border border-amber-400/50 flex items-center justify-center shrink-0">
                <Warning size={22} weight="fill" className="text-amber-300" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="text-[10px] font-bold tracking-[0.22em] uppercase text-amber-300/85">
                  Admin yêu cầu cập nhật avatar
                </div>
                <div className="text-[16px] font-black text-white leading-tight mt-0.5">
                  Vui lòng thay ảnh chân dung
                </div>
                {proposal.proposed_by_name ? (
                  <div className="text-[11px] text-white/55 mt-0.5">
                    Đề xuất bởi: <span className="text-white/85 font-bold">{proposal.proposed_by_name}</span>
                  </div>
                ) : null}
              </div>
            </div>

            <div className="px-5 py-4 space-y-3.5 overflow-y-auto">
              {phase === "intro" && (
                <>
                  {chips.length > 0 ? (
                    <div className="flex flex-wrap gap-1.5">
                      {chips.map((c) => (
                        <span
                          key={c}
                          className="px-2.5 h-7 rounded-full bg-amber-500/15 border border-amber-400/35 text-amber-200 text-[11px] font-bold flex items-center"
                          data-testid={`avatar-proposal-chip-${c}`}
                        >
                          {c}
                        </span>
                      ))}
                    </div>
                  ) : null}

                  {proposal.reason ? (
                    <div className="rounded-xl border border-white/10 bg-white/[0.04] p-3">
                      <div className="text-[10px] font-bold tracking-wider text-white/45 uppercase mb-1">
                        Ghi chú từ admin
                      </div>
                      <p className="text-[12.5px] text-white/85 leading-relaxed whitespace-pre-wrap">
                        {proposal.reason}
                      </p>
                    </div>
                  ) : null}

                  {proposal.sample_image_url ? (
                    <div>
                      <div className="text-[10px] font-bold tracking-wider text-white/45 uppercase mb-1.5 flex items-center gap-1.5">
                        <ImageSquare size={11} weight="bold" />
                        Ảnh tham khảo từ admin
                      </div>
                      <img
                        src={proposal.sample_image_url}
                        alt="Sample"
                        className="w-full max-h-52 object-contain rounded-2xl border border-white/10 bg-black/30"
                        data-testid="avatar-proposal-sample-img"
                      />
                    </div>
                  ) : null}

                  <div className="rounded-xl border border-blue-400/25 bg-blue-500/[0.06] p-3 flex items-start gap-2.5">
                    <ShieldCheck size={14} weight="bold" className="text-blue-300 mt-0.5 shrink-0" />
                    <p className="text-[11.5px] text-blue-100/85 leading-snug">
                      Hệ thống dùng AI để kiểm tra ảnh phải là <strong>chân dung người thật</strong>.
                      Vui lòng chọn ảnh rõ mặt, đủ sáng. Đây là <strong>yêu cầu bắt buộc</strong> —
                      thông báo sẽ hiện lại cho tới khi bạn cập nhật xong ảnh.
                    </p>
                  </div>

                  {/* v16 — Zalo BTC contacts để VĐV liên hệ xác minh điểm trình.
                      v16.5 — Ưu tiên snapshot `proposal.verify_zalos`.
                      v16.17 Phase C.14.b — Refactor style GIỐNG HỆT rating-video
                      modal: mỗi Zalo 1 dòng, icon 44×44, có SĐT + 2 nút Zalo+Gọi. */}
                  {(() => {
                    const list = snapshotZalos.length > 0
                      ? snapshotZalos
                      : zaloBtcContacts.map((m) => ({
                          name: m.name,
                          role: m.role,
                          zalo_href: m.zalo?.href,
                          tournament_name: m.tournament_name,
                        }));
                    if (list.length === 0) return null;
                    return (
                      <div
                        className="rounded-2xl border border-[#0068ff]/30 bg-[#0068ff]/[0.08] p-3 space-y-2"
                        data-testid="avatar-proposal-zalo-btc-list"
                      >
                        <div className="text-[11.5px] font-black text-blue-200 leading-snug">
                          Vui lòng liên hệ BTC để xác minh điểm trình
                        </div>
                        <div className="space-y-1.5">
                          {list.map((z, i) => {
                            const parsed = parseZaloContact(z.zalo_href);
                            const phone = parsed?.phone || null;
                            return (
                              <div
                                key={i}
                                className="flex items-center gap-2.5 rounded-xl bg-white/[0.04] hover:bg-white/[0.08] border border-white/10 p-2 transition"
                                data-testid={`avatar-proposal-zalo-item-${i}`}
                              >
                                <a
                                  href={z.zalo_href}
                                  target="_blank"
                                  rel="noopener noreferrer"
                                  className="w-11 h-11 rounded-full bg-[#0068ff] flex items-center justify-center shrink-0 shadow-[0_2px_10px_rgba(0,104,255,0.4)] hover:brightness-110 transition"
                                  title={`Chat Zalo ${z.name}`}
                                  data-testid={`avatar-proposal-zalo-avatar-${i}`}
                                >
                                  <img src={ZALO_ICON_URL} alt="Zalo" className="w-6 h-6" />
                                </a>
                                <div className="flex-1 min-w-0">
                                  <div className="text-[12.5px] font-black text-white truncate">
                                    {z.name}
                                  </div>
                                  <div className="text-[10.5px] text-white/55 truncate">
                                    {z.role}
                                    {z.tournament_name ? ` · ${z.tournament_name}` : ""}
                                  </div>
                                  {phone && (
                                    <div className="text-[10.5px] text-blue-200/75 font-mono tabular-nums mt-0.5">
                                      {phone}
                                    </div>
                                  )}
                                </div>
                                <div className="flex items-center gap-1.5 shrink-0">
                                  <a
                                    href={z.zalo_href}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="h-8 px-2.5 rounded-lg bg-[#0068ff]/25 hover:bg-[#0068ff]/40 border border-[#0068ff]/50 text-[10.5px] font-black text-blue-100 inline-flex items-center gap-1 transition"
                                    title={`Chat Zalo ${z.name}`}
                                    data-testid={`avatar-proposal-zalo-chat-${i}`}
                                  >
                                    Zalo <ArrowRight size={10} weight="bold" />
                                  </a>
                                  {phone && (
                                    <a
                                      href={`tel:${phone}`}
                                      className="h-8 w-8 rounded-lg bg-emerald-500/25 hover:bg-emerald-500/40 border border-emerald-400/50 text-emerald-100 inline-flex items-center justify-center transition"
                                      title={`Gọi ${z.name}`}
                                      data-testid={`avatar-proposal-zalo-call-${i}`}
                                    >
                                      <Phone size={12} weight="fill" />
                                    </a>
                                  )}
                                </div>
                              </div>
                            );
                          })}
                        </div>
                      </div>
                    );
                  })()}
                </>
              )}

              {phase === "upload" && (
                <>
                  {rejectInfo ? (
                    <div
                      className="rounded-xl border border-red-500/45 bg-red-500/[0.10] p-3.5 flex items-start gap-2.5"
                      data-testid="avatar-proposal-reject-banner"
                    >
                      <Warning size={18} weight="fill" className="text-red-300 mt-0.5 shrink-0" />
                      <div className="flex-1 min-w-0">
                        <div className="text-[12.5px] font-black text-red-200">
                          Ảnh chưa hợp lệ
                        </div>
                        <p className="text-[11.5px] text-red-100/85 mt-1 leading-snug">
                          {rejectInfo.reason}
                        </p>
                        {Number.isFinite(rejectInfo.confidence) ? (
                          <div className="text-[10px] text-red-200/55 mt-1">
                            Độ tin cậy AI: {(Number(rejectInfo.confidence) * 100).toFixed(0)}%
                          </div>
                        ) : null}
                        <button
                          type="button"
                          onClick={() => setShowGuide(true)}
                          className="mt-2 text-[11px] font-bold text-amber-300 underline underline-offset-2 hover:text-amber-200 transition"
                          data-testid="avatar-proposal-open-guide"
                        >
                          Xem hướng dẫn chụp avatar chuẩn →
                        </button>
                      </div>
                    </div>
                  ) : null}

                  <button
                    type="button"
                    onClick={onPickFile}
                    disabled={uploading}
                    className="w-full rounded-2xl border-2 border-dashed border-amber-400/35 hover:border-amber-400/60 bg-amber-500/[0.04] text-white py-6 flex flex-col items-center justify-center gap-2 transition disabled:opacity-50"
                    data-testid="avatar-proposal-pick-file-btn"
                  >
                    {uploading ? (
                      <Spinner size={28} className="animate-spin text-amber-300" />
                    ) : (
                      <UserCircle size={32} weight="duotone" className="text-amber-300" />
                    )}
                    <div className="text-[13px] font-black">
                      {uploading ? "Đang kiểm tra ảnh…" : "Chọn ảnh chân dung mới"}
                    </div>
                    <div className="text-[10.5px] text-white/55">
                      JPG/PNG/WebP · tối đa 5 MiB · sẽ tự crop vuông 300px
                    </div>
                  </button>
                  <input
                    ref={fileRef}
                    type="file"
                    accept="image/*"
                    className="hidden"
                    onChange={onFileChange}
                    data-testid="avatar-proposal-file-input"
                  />

                  {/* Phase 85.A.89 — Progress bar khi đang upload/phân tích */}
                  {uploading && uploadProgress.stage !== "idle" ? (
                    <div
                      className="rounded-2xl border border-amber-400/40 bg-gradient-to-br from-amber-500/10 to-orange-500/[0.04] p-4 space-y-2.5"
                      data-testid="avatar-proposal-progress"
                    >
                      <div className="flex items-center justify-between gap-2">
                        <div className="flex items-center gap-2 min-w-0">
                          <span className="w-4 h-4 rounded-full border-2 border-amber-300 border-t-transparent animate-spin shrink-0" />
                          <span
                            className="text-[12.5px] font-bold text-amber-100 truncate"
                            data-testid="avatar-proposal-progress-label"
                          >
                            {uploadProgress.label || "Đang xử lý…"}
                          </span>
                        </div>
                        <span
                          className="text-[13px] font-black text-amber-300 tabular-nums shrink-0"
                          data-testid="avatar-proposal-progress-pct"
                        >
                          {Math.min(100, Math.max(0, Math.round(uploadProgress.pct || 0)))}%
                        </span>
                      </div>
                      <div className="h-2.5 rounded-full bg-black/40 overflow-hidden border border-white/5">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-amber-500 via-amber-400 to-emerald-400 transition-all duration-500 ease-out"
                          style={{ width: `${Math.min(100, Math.max(0, uploadProgress.pct || 0))}%` }}
                        />
                      </div>
                      <div className="text-[10.5px] text-white/55 leading-snug">
                        💡 AI đang phân tích khuôn mặt để đảm bảo ảnh hợp lệ — vui lòng chờ trong giây lát.
                      </div>
                    </div>
                  ) : null}
                </>
              )}
            </div>

            <div className="px-5 py-4 flex items-center gap-2 border-t border-white/8 bg-black/30">
              {phase === "intro" ? (
                <button
                  type="button"
                  onClick={handleAccept}
                  disabled={busy}
                  className="w-full h-11 px-4 rounded-xl bg-gradient-to-r from-[#FF8A00] to-[#FF9F1C] hover:shadow-[0_8px_20px_rgba(255,138,0,0.4)] text-white text-[12.5px] font-black transition active:scale-[0.98] disabled:opacity-50 flex items-center justify-center gap-1.5"
                  data-testid="avatar-proposal-accept-btn"
                >
                  <CameraPlus size={14} weight="fill" />
                  {busy ? "Đang xử lý…" : "Đồng ý cập nhật ngay"}
                  <ArrowRight size={13} weight="bold" />
                </button>
              ) : (
                <div
                  className="w-full text-center text-[11px] font-bold text-amber-200/80 flex items-center justify-center gap-1.5"
                  data-testid="avatar-proposal-mandatory-hint"
                >
                  <Warning size={12} weight="fill" className="text-amber-300" />
                  Bắt buộc cập nhật — hãy chọn ảnh chân dung mới để hoàn tất
                </div>
              )}
            </div>
          </motion.div>
        </motion.div>
      </AnimatePresence>

      <AvatarFailureGuideModal
        open={showGuide}
        onClose={() => setShowGuide(false)}
        onRetry={onPickFile}
        errorDetail={rejectInfo?.reason}
      />

      {/* Phase 85.A.87 — Wizard consent gating: tự render overlay khi vào
          phase upload, user kéo slider → consentPassed=true → wizard tắt
          → user TỰ tap button "Chọn ảnh chân dung mới" (trực tiếp gọi
          fileRef.click). Pattern này tránh setTimeout/closing-modal break
          iOS Safari user-gesture chain. */}
      <UploadConsentWizard
        open={phase === "upload" && !consentPassed && !aiConsentPending}
        type="avatar"
        onClose={() => {
          // User đóng wizard chưa agree → quay lại phase intro.
          setPhase("intro");
        }}
        onAgree={() => setConsentPassed(true)}
      />

      {/* v16.60.16 — Apple 5.1.1(ii) AI consent (gate trước wizard).
          v16.61.2 — Không đồng ý → BỎ wizard, upload thường không qua AI.
          Nút X → quay lại phase intro. */}
      <AIConsentModal
        open={aiConsentPending}
        onAgree={() => {
          setAiConsent("granted");
          setAiConsentPending(false);
        }}
        onDecline={() => {
          setAiConsent("declined");
          setAiConsentPending(false);
          setConsentPassed(true);
        }}
        onCancel={() => {
          setAiConsentPending(false);
          setPhase("intro");
        }}
      />

      {/* v16.61.3 — Popup kết quả AI xét duyệt avatar. Khi user đóng
          (nếu PASS/SKIPPED) → gọi onResolved để đóng proposal modal.
          Nếu FAIL → chỉ đóng popup, giữ proposal modal để user thử lại. */}
      <AvatarAIResultModal
        open={!!aiResult}
        onClose={() => {
          const payload = aiResult?._resolvedPayload;
          const wasSuccess = aiResult?.verdict !== "fail";
          setAiResult(null);
          if (wasSuccess) {
            onResolved?.(payload);
          }
        }}
        verdict={aiResult?.verdict}
        reason={aiResult?.reason}
        confidence={aiResult?.confidence}
      />
    </>
  );
}
